import type {
  DisplayConfig,
  RowEdit,
  RowEditInput,
  SearchedMatch,
} from "@models/job";
import type { MappingResultRow, TmVariant } from "@models/mappingResult";
import type { PatchRequestViewModel } from "@models/revision";

export type RowSaveState = "idle" | "saving" | "saved" | "failed";

export interface RowSaveStatus {
  state: RowSaveState;
  error?: string;
}

export interface BulkEditEntry {
  rowIndex: number;
  edit: RowEditInput;
}

export interface BulkEditResult {
  succeeded: number;
  failed: number;
}

export interface UseJobResultsState {
  rows: MappingResultRow[];
  edits: Map<number, RowEdit>;
  saveStates: Map<number, RowSaveStatus>;
  cursor: string | null;
  display: DisplayConfig | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  notReady: boolean;
  applyEdit: (rowIndex: number, edit: RowEditInput) => void;
  clearEdit: (rowIndex: number) => void;
  retrySave: (rowIndex: number) => void;
  /** Retry every unsaved edit that is not already being saved. */
  retryAllFailed: () => void;
  /** Drop every unsaved edit; rows fall back to the server's state. */
  discardUnsaved: () => void;
  /** Wait (up to `timeoutMs`) for the saves in flight to settle, then return
   *  the rows whose edits are still unsaved. */
  flushSaves: (timeoutMs: number) => Promise<number[]>;
  bulkApplyEdits: (entries: BulkEditEntry[]) => Promise<BulkEditResult>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  addRow: (
    itemMpId: number,
    value: string,
    searched?: {
      itemMpText: string;
      matches: SearchedMatch[];
      /** Search stages that fell back; gates the server-side "not in RMI"
       *  warning (see `SearchResponse.degraded`). */
      degraded?: string[];
      /** Vehicle variant selected by the reviewer for a TM manual row. */
      variant?: TmVariant;
    },
  ) => Promise<void>;
  /** Merge one server row locally (e.g. after a revision restore). */
  applyServerRow: (row: MappingResultRow) => void;
}

export const toPatchPayload = (
  edit: RowEditInput,
): PatchRequestViewModel => {
  const payload: PatchRequestViewModel = { reviewStatus: edit.reviewStatus };
  if (edit.chosenRank !== undefined) payload.chosenRank = edit.chosenRank;
  if (edit.text !== undefined) payload.text = edit.text;
  return payload;
};
